import React, { useState } from 'react';

import { css } from '@emotion/react';
import { useParams } from 'react-router-dom';
import { color, Icon, Modal, spacing, Typography } from '@uniquegood/realworld-studio-design';
import FloatingNavigation, { FloatingNavigationProps } from '@components/FloatingNavigation';
import { AxiosError } from 'axios';
import { coreApi } from '@/api';
import { Identity, ListStyleTypeDisc, PaddingZero } from '@/styles';
import { container650Style } from '@/styles/containerStyles';
import {
  useChatbot,
  useMission,
  useScenarios,
  useQuest,
  useItem,
  useDidUpdateEffect,
  useModalState,
  usePromotionModal,
  usePlanLimits,
  useRequest
} from '@/hooks';
import { PromotionTriggerLocation } from '@/track';
import { toast } from '@/utils';
import BottomFloatingBar, { BOTTOM_FLOATING_BAR_HEIGHT } from '../BottomFloatingBar';
import { ActionEditor, ReactionEditor, FailureReactionEditor } from './components';
import { useActionHandler } from './hooks';
import { IAvailabel } from './types';
import NumberPreview from './components/NumberPreview';
import QuestOperationPreview from './components/QuestOperationPreview';
import { BottomFloatingBarExtendStyle } from './styles';
import RouteModal from '../RouteModal';
import { validateHiddenNextQuestReveal } from '@/pages/missions/questValidation';

interface ActionHandlerProps extends IAvailabel {
  isEmbedded?: boolean;
}

const ActionHandler = ({ availabelType, isEmbedded = false }: ActionHandlerProps) => {
  const { appId, questId, projectId, chatbotId, sceneId } = useParams<AppParam>();

  const { scenarios } = useScenarios();
  const { missions } = useMission();
  const { items } = useItem();
  const { items: chatbots, mutate: mutateChatbots } = useChatbot();
  const { quests, mutate: mutateQuests } = useQuest();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { actionHandler, setActionHandler } = useActionHandler({
    availabelType
  });

  const { modal, openModal, closeModal } = useModalState();
  const { openPromotionModal } = usePromotionModal();

  const reactionDefinitions = actionHandler.reactionDefinitions ?? [];
  const failureCommands = actionHandler.failureCommands ?? [];

  const [isDirty, setIsDirty] = useState(false);
  const [validationErrorMessages, setValidationErrorMessages] = useState<string[]>([]);

  const { plan, hasReached } = usePlanLimits();

  useDidUpdateEffect(() => {
    setIsDirty(false);
  }, [questId, sceneId]);

  const setReactionDefinitions: React.Dispatch<
    React.SetStateAction<ActionHandler['reactionDefinitions']>
  > = (setStateAction) => {
    setActionHandler((actionHandler) => {
      setIsDirty(true);
      if (typeof setStateAction === 'function')
        return {
          ...actionHandler,
          reactionDefinitions: setStateAction(actionHandler.reactionDefinitions ?? [])
        };

      return { ...actionHandler, reactionDefinitions: setStateAction };
    });
  };

  const setFailureCommands: React.Dispatch<React.SetStateAction<ActionHandler['failureCommands']>> =
    (setStateAction) => {
      setIsDirty(true);
      setActionHandler((actionHandler) => {
        if (typeof setStateAction === 'function')
          return {
            ...actionHandler,
            failureCommands: setStateAction(actionHandler.failureCommands ?? [])
          };

        return { ...actionHandler, failureCommands: setStateAction };
      });
    };

  const displayLimitExceededModal = (plan: PlanType) => {
    let content;

    if (plan === 'Free') {
      content =
        '요금제가 무료로 전환되어 콘텐츠 수정사항을 저장할 수 없습니다. 콘텐츠를 3개 이하로 줄이거나 요금제를 업그레이드해 주세요.';
    } else if (plan === 'Pro') {
      content =
        '요금제가 프로로 전환되어 콘텐츠 수정사항을 저장할 수 없습니다. 콘텐츠를 10개 이하로 줄이거나 요금제를 업그레이드해 주세요.';
    }

    // content가 없으면 모달을 표시하지 않음
    if (!content) {
      return;
    }

    openModal({
      title: '수정사항을 저장할 수 없어요',
      children: <Typography type="body">{content}</Typography>,
      primaryAction: {
        content: '확인',
        onAction: closeModal
      },
      secondaryAction: {
        content: '요금제 업그레이드',
        onAction: () => {
          closeModal();
          openPromotionModal({
            triggerLocation: PromotionTriggerLocation.content_save_blocked_content_limit
          });
        }
      }
    });
  };

  const { data: projects } = useRequest<CommonResponseModel<Project[]>>(
    `/apps/${appId}/projects`,
    {},
    {
      headers: {
        'x-rwd-api-version': '1.1'
      }
    }
  );
  const projectCount = projects?.data?.length ?? 0;

  const onSubmit = async () => {
    let hiddenNextQuestRevealError: string | undefined;

    // 퀘스트 저장
    if (availabelType === 'quest') {
      const isProjectLimitReached = hasReached('projects', projectCount);
      if (isProjectLimitReached && (plan === 'Free' || plan === 'Pro')) {
        displayLimitExceededModal(plan);
        return;
      }

      hiddenNextQuestRevealError = validateHiddenNextQuestReveal({
        missions,
        currentQuestId: Number(questId),
        reactionDefinitions,
        failureCommands
      });
    }

    setValidationErrorMessages(hiddenNextQuestRevealError ? [hiddenNextQuestRevealError] : []);
    setIsSubmitting(true);
    const requestUrl =
      availabelType === 'quest'
        ? `/apps/${appId}/quests/${questId}/action`
        : `/apps/${appId}/projects/${projectId}/chatbots/${chatbotId}/scenes/${sceneId}/action`;

    const payload: Omit<ActionHandler, 'id'> = {
      ...actionHandler,
      failureCommands,
      reactionDefinitions
    };

    try {
      await coreApi.put(requestUrl, payload);

      setIsDirty(false);

      if (availabelType === 'chatbot') {
        await mutateChatbots();
      } else {
        await mutateQuests();
      }

      toast({ message: '저장 완료!' });
    } catch (error) {
      const errorTitle = (error as AxiosError<{ errorTitle: string }>)?.response?.data.errorTitle;
      if (errorTitle === 'NotAllowedPlan') {
        openModal({
          title: '수정사항을 저장할 수 없어요',
          children: (
            <>
              <Typography as="p" type="body">
                무료 요금제에서 제한된 기능이 포함되어 있어 저장이 불가해요.
              </Typography>
              <Typography as="p" type="body">
                아래 중 하나를 선택하여 진행해 주세요.
              </Typography>
              <Typography as="p" type="body">
                1. 요금제 업그레이드
              </Typography>
              <Typography as="p" type="body">
                2. [미션 &gt; 퀘스트]에서 사용 불가능한 기능 제거
              </Typography>
              <Typography
                as="p"
                type="body"
                cssStyle={css`
                  margin-top: 24px;
                  color: #d30000;
                `}
              >
                무료 요금제 제한 기능
              </Typography>
              <ul css={ListStyleTypeDisc}>
                <li>
                  <Typography as="p" type="body">
                    디바이스 연동 도구 (NFC 태그)
                  </Typography>
                </li>
                <li>
                  <Typography as="p" type="body">
                    카메라 기반 실감 기술 도구 (프레임 촬영, QR, AR, 필터 렌즈, 오버랩)
                  </Typography>
                </li>
                <li>
                  <Typography as="p" type="body">
                    위치 기반 상호 작용 도구를 사용한 경우 (GPS 특정 장소 이동)
                  </Typography>
                </li>
                <li>
                  <Typography as="p" type="body">
                    아이템 - 30개 초과
                  </Typography>
                </li>
              </ul>
            </>
          ),
          primaryAction: {
            content: '확인',
            onAction: () => {
              closeModal();
            }
          },
          secondaryAction: {
            content: '요금제 업그레이드',
            onAction: () => {
              closeModal();
              openPromotionModal({
                triggerLocation: PromotionTriggerLocation.content_save_blocked_action_usage
              });
            }
          }
        });
      } else {
        toast({ message: '서버 오류로 저장에 실패했어요' });
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const leftElementWidth = getLeftElementWidth({ availabelType, isEmbedded });

  const navigationReactionItems = reactionDefinitions.map((_, index) => {
    const isSingleReaction = reactionDefinitions.length === 1;
    return {
      content: `조건별 리액션 ${!isSingleReaction ? index + 1 : ''}`,
      fragment: `reaction-definition-${index + 1}`,
      color: `${color.palette_secondary_blue_shade_02}`
    };
  });

  const floatingNavigationItems: FloatingNavigationProps['items'] = [
    {
      content: '플레이어 액션',
      fragment: 'player-action',
      color: `${color.palette_primary_purple_100}`
    },
    ...navigationReactionItems,
    {
      content: '오답 시 리액션',
      fragment: 'wrong-reaction',
      color: `${color.action_critical_default}`
    }
  ];

  return (
    <>
      <Modal {...modal} />
      <RouteModal when={isDirty} loading={isSubmitting} onSubmit={onSubmit} />
      {availabelType === 'quest' && !isEmbedded && (
        <FloatingNavigation
          items={floatingNavigationItems}
          cssStyle={FloatingNavigationBarStyle}
          offset={24}
        />
      )}
      <div
        css={[
          container650Style,
          PaddingZero,
          CardSpacing,
          // Hack
          availabelType === 'chatbot'
            ? css`
                && {
                  margin-bottom: calc(${BOTTOM_FLOATING_BAR_HEIGHT} + ${spacing.margin.xlarge2});
                }
              `
            : Identity
        ]}
      >
        {validationErrorMessages.length > 0 && (
          <div css={ValidationErrorMessageStyle} role="alert">
            <Icon icon="exclamation_circle_solid" size="20px" color={color.icon_critical} />
            <div>
              {validationErrorMessages.map((message) => (
                <Typography key={message} as="p" type="body" textColor="critical">
                  {message}
                </Typography>
              ))}
            </div>
          </div>
        )}

        {availabelType === 'quest' && (
          <QuestOperationPreview
            actionType={actionHandler.actionType}
            actionParameters={actionHandler.actionParameters}
            reactionDefinitions={reactionDefinitions}
            failureCommands={failureCommands}
          />
        )}

        {availabelType === 'quest' && (
          <NumberPreview availabelType={availabelType} type="action" id="player-action">
            <ActionEditor
              setIsDirty={setIsDirty}
              actionParameters={actionHandler?.actionParameters}
              actionType={actionHandler?.actionType}
              setActionHandler={setActionHandler}
              items={items}
            />
          </NumberPreview>
        )}

        <NumberPreview availabelType={availabelType} type="reaction" id="reaction-definition-1">
          <ReactionEditor
            params={reactionDefinitions}
            setParams={setReactionDefinitions}
            scenarios={scenarios}
            missions={missions}
            quests={quests}
            items={items}
            chatbots={chatbots}
            availabelType={availabelType}
          />
        </NumberPreview>

        <NumberPreview availabelType={availabelType} type="failReaction">
          <FailureReactionEditor
            commands={failureCommands}
            setCommands={setFailureCommands}
            scenarios={scenarios}
            missions={missions}
            quests={quests}
            items={items}
            chatbots={chatbots}
            availabelType={availabelType}
          />
        </NumberPreview>
      </div>

      <BottomFloatingBar
        loading={isSubmitting}
        disabled={isSubmitting}
        cssStyle={
          isEmbedded ? EmbeddedBottomBarStyle : BottomFloatingBarExtendStyle({ leftElementWidth })
        }
        onSave={onSubmit}
      />
    </>
  );
};

export default ActionHandler;

function getLeftElementWidth({ availabelType, isEmbedded }: ActionHandlerProps) {
  if (isEmbedded) {
    return '0px';
  }

  if (availabelType === 'quest') {
    return '306px';
  }

  return '186px';
}

const CardSpacing = css`
  & > div:not(:last-of-type) {
    margin-bottom: 48px;
  }
`;

const ValidationErrorMessageStyle = css`
  display: flex;
  align-items: flex-start;
  gap: ${spacing.common.small};
  padding: ${spacing.common.medium};
  margin-bottom: ${spacing.common.medium};
  border: 1px solid ${color.border_critical_subdued};
  border-radius: ${spacing.borderRadius.medium};
  background-color: ${color.surface_critical_subdued};
`;

const FloatingNavigationBarStyle = css`
  @media (max-width: 1150px) {
    display: none;
  }
  display: block;
  position: fixed;
  left: 1054px;
  width: 160px;
`;

const EmbeddedBottomBarStyle = css`
  && {
    width: auto;
    height: auto;
    right: ${spacing.common.medium};
    bottom: ${spacing.common.medium};
    padding: 0;
    background-color: transparent;
    box-shadow: none;
  }
`;
